import { keyframes } from '@emotion/react';
import theme from '@styles/theme';
import reusables from '@styles/theme/reusables';

const OpenAnimation = keyframes({
  from: {
    height: '0',
    visibility: 'hidden',
  },
  to: {
    visibility: 'visible',
    height: '100vh',
  },
});
const CloseAnimation = keyframes({
  from: {
    height: '100vh',
    visibility: 'visible',
  },
  to: {
    height: '0',
    visibility: 'hidden',
  },
});

export default {
  navigationContainer: {
    height: '100vw',
    width: '100vw',
  },
  navigationContainerMobile: (isTransparent: boolean, backgroundColor: string | null, isTextWhite: boolean) => {
    const withGradient = isTextWhite ? theme.background.navGradient : 'transparent';
    const background = backgroundColor || withGradient;
    return {
      display: ['block', 'block', 'block', 'block', 'none'],
      backgroundBlendMode: 'multiply',
      width: '100%',
      height: 'auto',
      margin: 'auto',
      zIndex: theme.zIndex.documentHigh,
      position: isTransparent ? 'absolute' : 'relative',
      left: 0,
      top: '0',
      background,
      borderBottom: isTransparent ? 'none' : `1px solid ${theme.rawColors.lightGrey300}`,
      transition: `background ${theme.transitions.time.normal} ${theme.transitions.animation.atomCurve}`,
      'a:hover, a:visited': {
        color: theme.rawColors.neutralBlack,
        textDecoration: 'none',
      },
    };
  },
  navigationMobilePadding: {
    padding: ['10px 16px', '10px 16px', '24px', '24px 0', '12px 0'],
  },
  navigationContainerDesktop: (
    active: boolean,
    isTransparent: boolean,
    backgroundColor: string,
    withBorder: boolean,
    isTextWhite: boolean,
  ) => {
    const withGradient = isTextWhite ? theme.background.navGradient : 'transparent';
    const background = backgroundColor || withGradient;

    const borderBottom = withBorder ? `1px solid ${theme.rawColors.lightGrey300}` : 'none';
    return {
      display: ['none', 'none', 'none', 'none', 'block'],
      width: '100%',
      backgroundBlendMode: 'multiply',
      zIndex: theme.zIndex.barMenu,
      position: isTransparent ? 'absolute' : 'relative',
      left: 0,
      top: 0,
      background: [background, background, `${active ? theme.background.megaNavHover : background}`],
      transition: `background ${theme.transitions.time.normal} ${theme.transitions.animation.atomCurve}`,
      ...reusables.mainTransition('background'),
      '&:hover': {
        background: [background, background, backgroundColor || theme.background.megaNavHover],
      },
      borderBottom: isTransparent ? 'none' : borderBottom,
      'a:hover, a:visited': {
        color: theme.rawColors.neutralBlack,
        textDecoration: 'none',
      },
    };
  },
  navigationContentContainer: {
    display: 'flex',
    alignItems: 'center',
    height: ['auto', 'auto', 'auto', 'auto', '100px'],
    padding: ['0', '0', '0', '0 40px', '0 40px'],
    margin: 'auto',
  },
  simpleNavigationContentContainer: {
    display: ['none', 'none', 'none', 'none', 'flex'],
    alignItems: 'center',
    width: '100%',
    height: ['auto', 'auto', 'auto', 'auto', '100px'],
    padding: ['0', '0', '0', '0 40px', '0 40px'],
    borderBottom: `solid 1px ${theme.rawColors.lightGreyBg}`,
    background: theme.background.megaNavHover,
  },
  simpleNavigationMobileContentContainer: {
    display: ['flex', 'flex', 'flex', 'flex', 'none'],
    alignItems: 'center',
    width: '100%',
    height: ['auto', 'auto', '100px'],
    padding: ['12px 0 11px 0', '24px 0'],
    borderBottom: `solid 1px ${theme.rawColors.lightGreyBg}`,
    background: theme.background.megaNavHover,
  },
  menuHamburgerIcon: {
    width: '24px',
    display: 'flex',
    alignItems: 'center',
  },
  loginIcon: {
    height: '26px',
    display: 'flex',
    marginLeft: ['auto', '25px', '25px', '25px', '30px'],
    h5: {
      mr: '6px',
      display: 'flex',
      alignItems: 'flex-end',
    },

    '.headings': {
      ...reusables.mainTransition('color', 'ease-in', '.1s'),
    },

    '&:focus .headings, &:hover .headings': {
      color: theme.rawColors.neutralBlack,
    },

    '&:focus .icon svg, &:hover .icon svg': {
      'path, rect': {
        fill: `${theme.rawColors.neutralBlack} !important`,
      },
    },
  },
  welcome: (color: string) => ({
    marginRight: '8px',
    fontFamily: theme.fonts.uniformMedium,
    fontSize: theme.fontSizes.h6,
    letterSpacing: '0.1em',
    lineHeight: '140%',
    color,
    textTransform: 'uppercase',
  }),
  menuSearchIcon: {
    height: '28px',
    marginLeft: 'auto',
    justifyContent: 'center',
    display: ['flex', 'flex', 'flex', 'none'],
  },
  menuSearchInputContainer: {
    flexGrow: 1,
    margin: '0 40px',
    padding: '2px 0 0',
    display: ['none', 'none', 'none', 'flex'],
    flexDirection: 'column',
    height: '54px',
    minWidth: '120px',
    position: 'relative',
  },
  menuSearchInput: (color: string, borderColor: string, isDesktop = false) => ({
    background: 'transparent',
    width: '100%',
    height: '50px',
    padding: '15px',
    borderRadius: 0,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor,
    textAlign: 'start',
    fontFamily: 'uniform',
    fontWeight: 'normal',
    fontSize: 'text',

    ...(isDesktop
      ? {
          ...reusables.mainTransition('border-color'),
          '&:hover': {
            borderColor: 'darkGrey700',
          },
        }
      : {}),

    '&::placeholder': {
      color,
    },
  }),
  cartIcon: (color: string) => ({
    height: '24px',
    width: '24px',
    display: 'flex',
    marginLeft: 'auto',
    justifyContent: 'center',
    '.a-voxCart__itemsCount': {
      color,
    },
  }),
  menuLogoDesktop: {
    height: '52px',
    width: '70px',
    marginRight: ['16px', '16px', '24px', '24px', '24px', '32px', '40px'],
  },
  menuLogoMobile: {
    height: ['32px', '32px', '32px', '32px', '52px', '52px'],
    width: ['43px', '43px', '43px', '43px', '70px', '70px'],
    marginRight: ['16px', '16px', '24px'],
    marginLeft: ['16px', '16px', '24px', '24px', '0'],

    a: {
      position: 'relative',
      display: 'inline-block',
      height: '100%',
      width: '100%',
    },
  },
  mainNavContentContainer: {
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '24px 0',
  },
  brandNavContainer: {
    display: 'flex',
    flexGrow: 1,
  },
  brandItem: (color: string) => ({
    marginRight: ['24px', '24px', '24px', '24px', '24px', '32px', '40px'],
    fontFamily: theme.fonts.uniformMedium,
    fontSize: theme.fontSizes.h6,
    letterSpacing: '0.1em',
    lineHeight: '140%',
    color,
    textTransform: 'uppercase',
    ...reusables.mainTransition('color'),
  }),
  productMenuContainer: {
    listStyle: 'none',
    width: 'max-content',
    display: 'flex',
    flexFlow: 'row wrap',
    mb: '0',
    columnGap: ['24px', '24px', '24px', '24px', '24px', '32px', '40px'],
  },
  productMenuItem: (color: string) => ({
    button: {
      fontFamily: theme.fonts.uniformMedium,
      fontSize: theme.fontSizes.text,
      letterSpacing: '0.1em',
      lineHeight: '140%',
      color,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      position: 'relative',
      ...reusables.mainTransition('color'),
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '100%',
        bottom: '-4px',
        height: '1px',
        opacity: 0,
        background: theme.rawColors.darkGrey700,
        ...reusables.mainTransition('opacity'),
      },
      '&:hover': {
        '&:before': {
          opacity: 1,
        },
      },
    },
  }),
  productActive: (isActive: boolean) => {
    const hoverElement = (active) => ({
      content: '""',
      position: 'absolute',
      width: '100%',
      opacity: active ? 1 : 0,
      bottom: '-4px',
      height: '1px',
      background: theme.rawColors.darkGrey700,
      ...reusables.mainTransition('opacity'),
    });

    return {
      cursor: 'pointer',
      position: 'relative',
      '&:before': hoverElement(isActive),

      '&:hover': {
        '&:before': hoverElement(true),
      },
    };
  },
  rightMenuContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: '18px 0 24px 0',
  },
  inspirationMenuItem: (color: string) => ({
    fontFamily: theme.fonts.uniformMedium,
    fontSize: theme.fontSizes.text,
    letterSpacing: '0.1em',
    lineHeight: '140%',
    color,
    width: 'max-content',
    textTransform: 'uppercase',
    position: 'relative',
    ...reusables.mainTransition('color'),
    '&:before': {
      content: '""',
      position: 'absolute',
      width: '100%',
      bottom: '-4px',
      height: '1px',
      opacity: 0,
      background: theme.rawColors.darkGrey700,
      ...reusables.mainTransition('opacity'),
    },
    '&:hover': {
      '&:before': {
        opacity: 1,
      },
    },
  }),
  inspirationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    paddingTop: '6px',
    columnGap: ['24px', '24px', '24px', '24px', '24px', '32px', '40px'],
    mt: 'auto',
    alignSelf: 'flex-end',
  },
  userLoggedContainer: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  loginButton: {
    height: 24,
    'span.button-children-wrapper': {
      alignItems: 'end',
      'h5.headings.heading__seven': {
        height: 18,
      },
    },
  },
  searchIcon: {
    position: 'absolute',
    pointerEvents: 'none',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  mainContainer: (isOpened) => ({
    opacity: isOpened ? 1 : 0,
    visibility: isOpened ? 'visible' : 'hidden',
    position: 'absolute',
    background: theme.rawColors.lightGrey300,
    animation: isOpened ? `${OpenAnimation} .45s both` : `${CloseAnimation} .45s both`,
    animationTimingFunction: 'cubic-bezier(.54, 0, .58, 1)',
    width: '100%',
    height: '100vh',
    zIndex: theme.zIndex.search,
    top: '0',
    left: '0',
    overflow: 'hidden auto',

    'a:hover': {
      textDecoration: 'none !important',
    },
  }),
};
